import React from 'react';
import { Typography } from '@mui/material';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// Overlay rows in the ON-SCREEN legend only (TGCLOUD-1670).
//
// This renders inside the legend Collapse as the last block, a SIBLING above the
// dates block, never inside it: that dates Box is the one on-screen element the
// export captures, so anything placed in it would reach the PDF. Nothing is
// added to the off-screen export legend mirror either. An overlay is a reading
// aid for the person at the screen, and the exported pedigree must look the same
// whether or not one was on.
//
// Rows appear only when the overlay actually marks something on this chart
// (Tim's legend honesty rule): a legend entry explaining a ring nobody can see
// invites the reader to hunt for it.
//
// The visual grammar is COPIED from VariantPathogenicityLegend, which is this
// block's immediate sibling in the column, rather than invented: a 1rem bold
// heading in title case, a 0.72rem caption at 0.7 opacity, rows in a 4px-gap
// column, a 10px glyph with an 8px gap, and a 0.85rem label. The first cut used
// the Overlays POPOVER's section style here instead (11px, uppercase, letter
// spaced, disabled grey) and wrapped the glyph in a 34px centring box, so the
// heading looked like a different kind of thing from "Diseases" and every row
// was indented past the swatches above it.

const GLYPH = 10;

/**
 * The mark as it is actually drawn on the canvas: a ring, solid for slot A and
 * dashed for slot B. SVG rather than a CSS border because a dashed border on a
 * 10px circle is left to the browser and renders as mush; here the dash length
 * is chosen so four dashes go round, which still reads as broken at this size.
 *
 * 10px wide with no wrapper, so the label after it starts at the same x as the
 * labels in every block above.
 */
const RingGlyph = ({ colour, dashed }) => {
  const r = (GLYPH - 2) / 2;
  const circumference = 2 * Math.PI * r;
  return (
    <svg
      width={GLYPH}
      height={GLYPH}
      viewBox={`0 0 ${GLYPH} ${GLYPH}`}
      style={{ flexShrink: 0, display: 'block' }}
      aria-hidden="true"
      data-ring-style={dashed ? 'dashed' : 'solid'}
    >
      <circle
        cx={GLYPH / 2}
        cy={GLYPH / 2}
        r={r}
        fill="none"
        stroke={colour}
        strokeWidth={2}
        strokeDasharray={
          dashed ? `${circumference / 8} ${circumference / 8}` : undefined
        }
      />
    </svg>
  );
};

const DataOverlayLegend = ({ slots = [] }) => {
  const t = useAutoTranslation();

  const rows = slots
    .map((slot, index) => ({ slot, index }))
    .filter(({ slot }) => {
      if (!slot?.enabled || !slot.results) return false;
      return slot.results.matches.some((m) => m.onCanvas);
    });

  if (!rows.length) return null;

  return (
    <div style={{ marginBottom: 10 }}>
      <Typography style={{ fontSize: '1rem', fontWeight: 'bold' }}>
        {t('Data Overlays')}
      </Typography>
      {/* The one thing a reader of this legend could reasonably get wrong. */}
      <Typography
        style={{ fontSize: '0.72rem', opacity: 0.7, marginBottom: 2 }}
      >
        {t('On screen only, never included in an export')}
      </Typography>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          marginTop: 4
        }}
      >
        {rows.map(({ slot, index }) => {
          const onCanvas = slot.results.matches.filter(
            (m) => m.onCanvas
          ).length;
          return (
            <div
              key={`${slot.presetId}-${index}`}
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <RingGlyph colour={slot.colour} dashed={index === 1} />
              <Typography style={{ fontSize: '0.85rem' }}>
                {t(slot.preset?.title || slot.results.title)}
                <span style={{ fontSize: '0.85rem', color: '#757575' }}>
                  {` (${onCanvas} ${t('on chart')})`}
                </span>
              </Typography>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DataOverlayLegend;
